import { Stack, Typography } from '@mui/material';

import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { Alert } from '@/components/notifications/Alert/Alert';
import type {
  FormField,
  FormSalesforceEvidenceExport,
  FormSalesforceFieldExport,
} from '@/modules/forms/formConfig';

import { useSalesforceBrand } from './brand';
import {
  answerDestinationCandidates,
  evidenceDestinationCandidates,
  SALESFORCE_EVIDENCE_EXPORT_OPTIONS,
  type SalesforceDestinationField,
  type SalesforceFormMapping,
} from './formMapping';
import type { SalesforceFieldDescription } from './metadata';

interface Props {
  field: FormField;
  mapping: SalesforceFormMapping;
  destinations?: SalesforceFieldDescription[];
  failed: boolean;
  onAnswerChange: (
    destination: SalesforceDestinationField,
    sentAs: FormSalesforceFieldExport['sentAs']
  ) => void;
  onAnswerRemove: (fieldAPIName: string) => void;
  onEvidenceChange: (
    evidence: FormSalesforceEvidenceExport | undefined
  ) => void;
}

export function SalesforceQuestionMappingEditor({
  field,
  mapping,
  destinations,
  failed,
  onAnswerChange,
  onAnswerRemove,
  onEvidenceChange,
}: Props) {
  const brand = useSalesforceBrand();
  const answer = field.exports?.salesforce;
  const answerCandidates = answerDestinationCandidates(
    mapping,
    field,
    destinations ?? []
  );
  const currentAnswer = answerCandidates.find(
    (candidate) => candidate.apiName === answer?.fieldAPIName
  );
  const evidence = answer?.evidence;
  const evidenceCandidates = evidenceDestinationCandidates(
    mapping,
    evidence?.fieldAPIName,
    destinations ?? []
  );

  if (failed) {
    return (
      <Alert severity="warning">
        {brand.name} fields could not be loaded. Reload the page to try again.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <SelectInput
        label="Answer field"
        value={answer?.fieldAPIName ?? ''}
        emptyValueDisplay="Do not export this answer"
        loading={!destinations}
        options={answerCandidates.map((destination) => ({
          value: destination.apiName,
          display: destination.label,
          secondaryText: destination.apiName,
        }))}
        helpText="Only unused fields that match this question type are available."
        onChange={(event) => {
          const apiName = String(event.target.value);
          if (!apiName) {
            if (answer) onAnswerRemove(answer.fieldAPIName);
            return;
          }
          const destination = answerCandidates.find(
            (candidate) => candidate.apiName === apiName
          );
          if (destination)
            onAnswerChange(
              destination,
              initialTranslations(field, destination, answer)
            );
        }}
      />
      {answer?.destination.type === 'picklist' && (
        <PicklistTranslations
          field={field}
          mapping={answer}
          values={
            currentAnswer?.type === 'picklist'
              ? currentAnswer.picklistValues
                  .filter((value) => value.active)
                  .map((value) => value.value)
              : []
          }
          onChange={(sentAs) => {
            if (currentAnswer) onAnswerChange(currentAnswer, sentAs);
          }}
        />
      )}
      {answer && (
        <Stack spacing={1}>
          <SelectInput
            label="Evidence field"
            value={evidence?.fieldAPIName ?? ''}
            emptyValueDisplay="Do not export evidence"
            loading={!destinations}
            options={evidenceCandidates.map((destination) => ({
              value: destination.apiName,
              display: destination.label,
              secondaryText: destination.apiName,
            }))}
            onChange={(event) => {
              const destination = evidenceCandidates.find(
                (candidate) => candidate.apiName === String(event.target.value)
              );
              onEvidenceChange(
                destination
                  ? {
                      fieldAPIName: destination.apiName,
                      type: destination.type,
                      includeReasoning: evidence?.includeReasoning ?? false,
                      includeQuotes: evidence?.includeQuotes ?? false,
                      includeCitations: evidence?.includeCitations ?? false,
                    }
                  : undefined
              );
            }}
          />
          {evidence &&
            SALESFORCE_EVIDENCE_EXPORT_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                id={`salesforce-evidence-${field.id}-${option.value}`}
                label={option.display}
                value={evidence[option.value]}
                onChange={(event) =>
                  onEvidenceChange({
                    ...evidence,
                    [option.value]: event.target.checked,
                  })
                }
              />
            ))}
        </Stack>
      )}
    </Stack>
  );
}

function PicklistTranslations({
  field,
  mapping,
  values,
  onChange,
}: {
  field: FormField;
  mapping: FormSalesforceFieldExport;
  values: string[];
  onChange: (sentAs: FormSalesforceFieldExport['sentAs']) => void;
}) {
  const brand = useSalesforceBrand();
  return (
    <Stack spacing={1}>
      <Typography variant="title-xs">Answer values</Typography>
      {(field.options ?? []).map((option) => {
        const translation = mapping.sentAs.find(
          (candidate) => candidate.formValue === option.value
        );
        const unavailableValue =
          translation && !values.includes(translation.salesforceValue)
            ? translation.salesforceValue
            : undefined;
        return (
          <SelectInput
            key={option.value}
            label={option.label || option.value}
            value={translation?.salesforceValue ?? ''}
            emptyValueDisplay={`Choose a ${brand.name} value`}
            required
            errorMessage={
              unavailableValue
                ? `Choose a current ${brand.name} value.`
                : undefined
            }
            options={[
              ...(unavailableValue
                ? [
                    {
                      value: unavailableValue,
                      display: `${unavailableValue} (unavailable)`,
                    },
                  ]
                : []),
              ...values.map((value) => ({ value, display: value })),
            ]}
            onChange={(event) =>
              onChange([
                ...mapping.sentAs.filter(
                  (candidate) => candidate.formValue !== option.value
                ),
                {
                  formValue: option.value,
                  salesforceValue: String(event.target.value),
                },
              ])
            }
          />
        );
      })}
    </Stack>
  );
}

function initialTranslations(
  field: FormField,
  destination: SalesforceDestinationField,
  current: FormSalesforceFieldExport | undefined
): FormSalesforceFieldExport['sentAs'] {
  if (destination.type !== 'picklist') return [];
  if (destination.apiName === current?.fieldAPIName) return current.sentAs;
  return (field.options ?? []).flatMap((option) =>
    destination.picklistValues.some(
      (candidate) => candidate.active && candidate.value === option.value
    )
      ? [{ formValue: option.value, salesforceValue: option.value }]
      : []
  );
}
